import { useQuery } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext, useEffect } from "react";
import { z } from "zod";
import { httpClient } from "@/api/client";
import { apiClient } from "@/api/endpoints";
import { getOrganisasjonerForUserOptions } from "@/api/generated/@tanstack/react-query.gen";
import usePersistantState, {
  getPersistantState,
} from "@/features/shared/usePersistantState";

export const INGEN_SELSKAP = {
  navn: "Ingen selskap",
  organisasjonsnummer: 0, // Use '0' as a special value allowing the user to update its session with no organizations without signing out first
} satisfies SelskapSchema;

export const selskapSchema = z.object({
  organisasjonsnummer: z.number(),
  navn: z.string(),
});

export type SelskapSchema = z.infer<typeof selskapSchema>;

type SelskapContextType = {
  isLoadingSelskaper: boolean;
  isErrorLoadingSelskaper: boolean;
  selskaper: SelskapSchema[];
  active: SelskapSchema | null;
  setActiveSelskap: (selskap: SelskapSchema) => void;
};

const SelskapContext = createContext<SelskapContextType | undefined>(undefined);

export const ActiveSelskapStorageKey = "activeSelskapKundeportal";

export const SelskapContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [activeSelskap, setActiveSelskap] =
    usePersistantState<SelskapSchema | null>(
      ActiveSelskapStorageKey,
      selskapSchema,
      getPersistantState(ActiveSelskapStorageKey, sessionStorage),
      sessionStorage,
    );

  const query = useQuery({
    ...getOrganisasjonerForUserOptions({
      client: httpClient,
    }),
  });

  const hovedenheter: SelskapSchema[] =
    query.data?.organisasjoner.map((he) => ({
      organisasjonsnummer: he.organisasjonsnummer as number,
      navn: he.navn,
    })) ?? [];
  const allSelskaper: SelskapSchema[] = [INGEN_SELSKAP, ...hovedenheter];

  useEffect(() => {
    const syncCache = async () => {
      try {
        const accountInfo = await apiClient.auth.getUserInfo();
        const sessionTilganger = accountInfo.organisasjonsTilgang;

        if (sessionTilganger == null) {
          setActiveSelskap(null);
          return;
        }

        if (
          sessionTilganger != null &&
          activeSelskap?.organisasjonsnummer !==
            sessionTilganger.organisasjonsnummer
        ) {
          // Cache is out of sync with session; update it to match the current session
          setActiveSelskap({
            organisasjonsnummer: sessionTilganger.organisasjonsnummer,
            navn: sessionTilganger.navn,
          });
        }
      } catch (error) {
        console.error("Failed to sync selskap cache with session:", error);
      }
    };

    // Sync on mount
    syncCache();
  }, [activeSelskap, setActiveSelskap]);

  return (
    <SelskapContext.Provider
      value={{
        isLoadingSelskaper: query.isLoading,
        isErrorLoadingSelskaper: query.isError,
        active: activeSelskap,
        setActiveSelskap: setActiveSelskap,
        selskaper: allSelskaper,
      }}
    >
      {children}
    </SelskapContext.Provider>
  );
};

export function useSelskaper() {
  const context = useContext(SelskapContext);
  if (!context) {
    throw new Error(
      "useSelskaper must be used within an SelskapContextProvider",
    );
  }
  return context;
}
